import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import { IInputTask } from 'interfaces/IInputTask';
import InputService from 'services/InputService';

interface UseDeleteInputTaskArgs {
  activeOrganisationId: string | null;
}

interface DeleteInputTaskVariables {
  inputId: string;
  taskId: string;
}

interface DeleteInputTaskContext {
  /** The task and where it sat, to put back if the delete fails. */
  previousTask: IInputTask | null;
  previousIndex: number;
}

/**
 * Deletes one task on any plan — not only the plan open in Planning — and
 * keeps the org's inputs cache in step. The counterpart of
 * useUpsertInputTask, used by the Nutrition lens (OP-2523) when removing a
 * product leaves its application empty.
 *
 * The task goes from the cache before the request is sent, the server's copy
 * of the plan is written in on success, and the task is put back where it
 * was on failure.
 */
export const useDeleteInputTask = ({
  activeOrganisationId,
}: UseDeleteInputTaskArgs) => {
  const queryClient = useQueryClient();
  const queryKey = [INPUTS_QUERY_KEY, activeOrganisationId];

  return useMutation<
    Awaited<ReturnType<typeof InputService.DeleteTask>>,
    unknown,
    DeleteInputTaskVariables,
    DeleteInputTaskContext
  >({
    mutationFn: async ({ inputId, taskId }) => {
      const clientNow = new Date().toISOString();
      return InputService.DeleteTask(inputId, taskId, clientNow);
    },
    onMutate: async ({ inputId, taskId }) => {
      if (!activeOrganisationId) return { previousTask: null, previousIndex: -1 };
      // A refetch landing mid-delete would put the task back on screen.
      await queryClient.cancelQueries({ queryKey });
      const tasks =
        queryClient
          .getQueryData<IInput[]>(queryKey)
          ?.find((input) => input.id === inputId)?.tasks ?? [];
      const previousIndex = tasks.findIndex((t) => t.taskId === taskId);
      queryClient.setQueryData<IInput[]>(queryKey, (current) =>
        current?.map((input) =>
          input.id === inputId
            ? {
                ...input,
                tasks: (input.tasks ?? []).filter((t) => t.taskId !== taskId),
              }
            : input
        )
      );
      return {
        previousTask: previousIndex >= 0 ? tasks[previousIndex] : null,
        previousIndex,
      };
    },
    onError: (_error, { inputId }, context) => {
      const previousTask = context?.previousTask;
      if (!activeOrganisationId || !previousTask) return;
      queryClient.setQueryData<IInput[]>(queryKey, (current) =>
        current?.map((input) => {
          if (input.id !== inputId) return input;
          const tasks = (input.tasks ?? []).filter(
            (t) => t.taskId !== previousTask.taskId
          );
          const at = Math.min(context.previousIndex, tasks.length);
          return {
            ...input,
            tasks: [...tasks.slice(0, at), previousTask, ...tasks.slice(at)],
          };
        })
      );
    },
    onSuccess: (response) => {
      // The server returns the saved plan, so it's written straight in rather
      // than invalidated (see useUpsertInputTask).
      const updatedInput = response?.data?.[0];
      if (!updatedInput || !activeOrganisationId) return;

      queryClient.setQueryData<IInput[]>(queryKey, (current) => {
        if (!current) return current;
        return current.map((item) =>
          item.id === updatedInput.id ? updatedInput : item
        );
      });
    },
  });
};

export default useDeleteInputTask;
